import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, MapPin, Video, Save, Link as LinkIcon, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';
import { CalendarEvent } from '../../hooks/useCalendar';
import { googleCalendarService } from '../../services/googleCalendar';
import { supabase } from '../../lib/supabase';
import { OFFICE_LOCATIONS } from '../../constants/officeLocations';


interface EditEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (eventData: any) => void;
  onDelete: (eventId: string) => void;
  event: CalendarEvent;
  calendars?: { id: string; name: string }[];
}

const EditEventModal: React.FC<EditEventModalProps> = ({ isOpen, onClose, onSave, onDelete, event, calendars = [] }) => {
  const [formData, setFormData] = useState({
    id: '',
    calendar_id: '',
    title: '',
    description: '',
    date: '',
    startTime: '',
    endTime: '',
    type: 'meeting' as 'meeting' | 'call' | 'appointment' | 'deadline' | 'other',
    location: '',
    meetingLink: '',
    participants: [] as { id: string; name: string; email: string }[],
    sendNotification: true,
    attendeeResponses: [] as any[],
    sendEmailInvites: false,
    emailMessage: '',
    // New fields
    event_theme: 'Fiziki Randevu',
    guest_name: '',
    guest_surname: '',
    guest_class: '',
    guest_phone: '',
    guest_email: '',
    interest_department: '',
    notes: '',
    color: '#BE185D',
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSendingReminder, setIsSendingReminder] = useState(false);

  // Local state for free-text input (allows spaces)
  const [guestFullName, setGuestFullName] = useState('');

  useEffect(() => {
    if (event) {
      const startTime = new Date(event.start_time);
      const endTime = new Date(event.end_time);

      setFormData({
        id: event.id,
        calendar_id: event.calendar_id || (calendars[0]?.id ?? ''),
        title: event.title,
        description: event.description || '',
        date: format(startTime, 'yyyy-MM-dd'),
        startTime: format(startTime, 'HH:mm'),
        endTime: format(endTime, 'HH:mm'),
        type: 'meeting',
        location: event.location || '',
        meetingLink: event.meeting_link || '',
        participants: event.attendees?.filter(a => !a.is_organizer && a.user_id).map(a => ({
          id: a.user_id!,
          name: a.name || 'Bilinmeyen',
          email: a.email || ''
        })) || [],
        sendNotification: true,
        attendeeResponses: event.attendees || [],
        sendEmailInvites: false,
        emailMessage: '',
        // New fields
        event_theme: event.event_theme || 'Fiziki Randevu',
        guest_name: event.guest_name || '',
        guest_surname: event.guest_surname || '',
        guest_class: event.guest_class || '',
        guest_phone: event.guest_phone || '',
        guest_email: event.guest_email || '',
        interest_department: event.interest_department || '',
        notes: event.notes || '',
        color: event.color || '#BE185D'
      });
      setGuestFullName(`${event.guest_name || ''} ${event.guest_surname || ''}`.trim());
    }
  }, [event]);

  const handleGenerateLink = async () => {
    setIsGenerating(true);
    try {
      await googleCalendarService.loadScripts();
      await googleCalendarService.authenticate();

      const startISO = new Date(`${formData.date}T${formData.startTime}:00`).toISOString();
      const endISO = new Date(`${formData.date}T${formData.endTime}:00`).toISOString();

      const link = await googleCalendarService.createMeeting(
        formData.title || 'Düzenlenen Toplantı',
        startISO,
        endISO
      );

      if (link) {
        setFormData(prev => ({ ...prev, meetingLink: link }));
      } else {
        throw new Error('Link oluşturulamadı.');
      }
    } catch (e: any) {
      console.error('Google Meet Error:', e);
      let msg = e.message || 'Bir hata oluştu.';
      if (msg.includes('Client ID')) {
        alert(msg);
      } else {
        alert(`Toplantı oluşturulamadı: ${msg}`);
      }
    } finally {
      setIsGenerating(false);
    }
  };


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await onSave(formData);

      // SMS Sending Logic removed - handled in useCalendar

    } catch (error) {
      console.error('Etkinlik güncelleme hatası:', error);
      alert('Etkinlik güncellenirken hata oluştu');
    }

    setIsSaving(false);
  };

  const handleSendReminder = async () => {
    if (!formData.guest_phone) {
      alert('Misafir telefonu kayıtlı değil.');
      return;
    }

    if (!await window.appConfirm('Hatırlatma SMS\'i göndermek istediğinize emin misiniz?')) return;

    setIsSendingReminder(true);
    try {
      const selectedLocation = OFFICE_LOCATIONS.find(l => l.name === formData.location);
      const officePhone = selectedLocation ? selectedLocation.phone : '0212 544 24 15';
      const formattedDate = new Date(formData.date + 'T' + formData.startTime).toLocaleDateString('tr-TR');
      const formattedTime = formData.startTime;

      const smsMessage = `Merhabalar, Pisa Egitim'den iletisime geciyoruz. Bugun gerceklesecek olan gorusmenizi hatirlatmak isteriz.\n\nTarih: ${formattedDate}\nSaat: ${formattedTime}\nGorusmelerimiz 45 dk.'dir.\nHerhangi bir degisiklik durumunda ${officePhone} numarali hattimizdan bize bilgi vermenizi rica ederiz.\nTesekkur eder, iyi gunler dileriz.`;

      const { data: userData } = await supabase.auth.getUser();

      await supabase.functions.invoke('send-sms', {
        body: {
          phones: [formData.guest_phone],
          message: smsMessage,
          userId: userData.user?.id,
          branchId: ''
        }
      });

      alert('Hatırlatma SMS\'i gönderildi.');
    } catch (error) {
      console.error('SMS Hatası:', error);
      alert('SMS gönderilirken hata oluştu.');
    } finally {
      setIsSendingReminder(false);
    }
  };

  const handleDelete = async () => {
    if (await window.appConfirm('Bu etkinliği silmek istediğinize emin misiniz?')) {
      setIsDeleting(true);

      try {
        await onDelete(event.id);
      } catch (error) {
        console.error('Etkinlik silme hatası:', error);
        alert('Etkinlik silinirken hata oluştu');
      }

      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Etkinliği Düzenle</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">

          {/* Calendar Select, Title, Date, Time Row */}
          <div className="space-y-4">
            {/* Calendar Select */}
            {calendars.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Takvim <span className="text-red-500">*</span>
                </label>
                <select
                  name="calendar_id"
                  value={formData.calendar_id}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {calendars.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Etkinlik Başlığı <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Örn: Öğrenci Danışmanlık Görüşmesi"
              />
            </div>

            {/* Date and Time Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tarih <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Başlangıç <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="time"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bitiş <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="time"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
            </div>
          </div>
        </div>

          <div className="grid grid-cols-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Etkinlik Teması
              </label>
              <select
                name="event_theme"
                value={formData.event_theme}
                onChange={handleChange}
                className="w-full px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Fiziki Randevu">Fiziki Randevu</option>
                <option value="Online Randevu">Online Randevu</option>
                <option value="Etkinlik">Etkinlik</option>
                <option value="Telefon Görüşmesi">Telefon Görüşmesi</option>
                <option value="Toplantı">Toplantı</option>
                <option value="Vize Randevusu">Vize Randevusu</option>
                <option value="Öğlen Arası">Öğlen Arası</option>
                <option value="İptal Olan Randevu">İptal Olan Randevu</option>
              </select>
            </div>
          </div>

          {/* Conditional Location/Link */}
          {formData.event_theme === 'Online Randevu' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Online Toplantı Linki
              </label>
              <div className="relative">
                <LinkIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  name="meetingLink"
                  value={formData.meetingLink}
                  onChange={handleChange}
                  placeholder="https://…"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="mt-2 flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleGenerateLink}
                  disabled={isGenerating}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isGenerating
                    ? 'bg-gray-300 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                  <Video className="w-4 h-4" />
                  {isGenerating ? 'Oluşturuluyor...' : 'Google Meet Linki Oluştur (Otomatik)'}
                </button>
              </div>
            </div>
          )}

          {['Fiziki Randevu', 'Etkinlik', 'Toplantı', 'Vize Randevusu', 'Öğlen Arası'].includes(formData.event_theme || '') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Konum
              </label>
              <div className="relative">
                <MapPin className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
                >
                  <option value="">Konum Seçin...</option>
                  {OFFICE_LOCATIONS.map(loc => (
                    <option key={loc.id} value={loc.name}>{loc.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Açıklama
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={2}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Etkinlik hakkında notlar..."
            />
          </div>

          {/* Event Details & Guest Info */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Etkinlik Detayları & Misafir Bilgileri</h4>

            <div className="grid grid-cols-1 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Misafir Adı Soyadı
                </label>
                <input
                  type="text"
                  value={guestFullName}
                  onChange={(e) => {
                    const val = e.target.value;
                    setGuestFullName(val);

                    const parts = val.trim().split(' ');
                    if (parts.length === 1) {
                      setFormData(prev => ({ ...prev, guest_name: parts[0], guest_surname: '' }));
                    } else if (parts.length > 1) {
                      const surname = parts.pop() || '';
                      const name = parts.join(' ');
                      setFormData(prev => ({ ...prev, guest_name: name, guest_surname: surname }));
                    } else {
                      setFormData(prev => ({ ...prev, guest_name: '', guest_surname: '' }));
                    }
                  }}
                  className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ad Soyad"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sınıf
                </label>
                <input
                  type="text"
                  name="guest_class"
                  value={formData.guest_class}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Örn. 12. Sınıf"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  İlgilendiği Bölüm
                </label>
                <input
                  type="text"
                  name="interest_department"
                  value={formData.interest_department}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Bölüm adı"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Telefon Numarası
                </label>
                <input
                  type="text"
                  name="guest_phone"
                  value={formData.guest_phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="5XX XXX XX XX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="guest_email"
                  value={formData.guest_email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="ornek@email.com"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notlar
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={2}
                className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Ek notlar..."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between space-x-3 pt-6 border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-all ${isDeleting
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
            >
              <span>{isDeleting ? 'Siliniyor...' : 'Sil'}</span>
            </button>
            <button
              type="button"
              onClick={handleSendReminder}
              disabled={isSendingReminder || !formData.guest_phone}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${!formData.guest_phone
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600'
                : isSendingReminder
                  ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400'
                  : 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400 dark:hover:bg-yellow-900/30'
                }`}
            >
              <MessageSquare className={`w-4 h-4 ${isSendingReminder ? 'animate-pulse' : ''}`} />
              <span>{isSendingReminder ? 'Gönderiliyor...' : 'Hatırlatma Gönder'}</span>
            </button>

            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-all ${isSaving
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                  : 'bg-blue-600 hover:bg-blue-700 text-white hover:scale-105 hover:shadow-lg hover:shadow-blue-600/25'
                  }`}
              >
                <Save className={`w-4 h-4 ${isSaving ? 'animate-pulse' : ''}`} />
                <span>{isSaving ? 'Kaydediliyor...' : 'Kaydet'}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEventModal;
